import { Fragment, createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ALL_FARMS, FarmContext } from '@context/FarmContext';
import { ConfirmDialog } from '@pages/management/shared/ConfirmDialog';
import { toast } from '@services/toast';
import { getNavigationArea } from './utils';
import { EmptyStateAction, EmptyStateCard, EmptyStateDescription, EmptyStateTitle, EmptyStateWrapper } from '@errors/ConfirmInfoError/style';

/**
 * Formulário financeiro aberto. `blockRouteExit`: formulário ligado à rota
 * (detalhe/ação em página) — sair da rota com ele alterado pede confirmação e
 * a aba avisa antes de recarregar/fechar. `dirty` ausente = alterado (os
 * formulários em diálogo não rastreiam campos tocados).
 */
type FormState = { submitting: boolean; dirty: boolean; blockRouteExit: boolean };

/** Estado de navegação de quem fecha a própria ação: não pede confirmação. */
export const FORM_CLOSED_STATE = { financialFormClosed: true } as const;

function closedByForm(state: unknown): boolean {
  return Boolean(state && typeof state === 'object' && (state as { financialFormClosed?: unknown }).financialFormClosed);
}
type NavigationState = {
  registerForm: (form: FormState) => () => void;
  ready: boolean;
  invalidLink: boolean;
  filterValues: Map<string, unknown> | null;
};

const NavigationContext = createContext<NavigationState>({
  registerForm: () => () => {},
  ready: true,
  invalidLink: false,
  filterValues: null,
});

/** Preserva só filtros de leitura ao remontar a página por troca de escopo. */
export function useFinancialFilterState<T>(name: string, initial: T | (() => T)): [T, Dispatch<SetStateAction<T>>] {
  const { filterValues } = useContext(NavigationContext);
  const { pathname } = useLocation();
  const key = `${pathname}:${name}`;
  const [value, setValue] = useState<T>(() => filterValues?.has(key)
    ? filterValues.get(key) as T
    : typeof initial === 'function' ? (initial as () => T)() : initial);
  const update = useCallback<Dispatch<SetStateAction<T>>>((next) => {
    setValue((current) => {
      const updated = typeof next === 'function' ? (next as (current: T) => T)(current) : next;
      filterValues?.set(key, updated);
      return updated;
    });
  }, [filterValues, key]);
  return [value, update];
}

/**
 * Registra também formulários em portais, sem depender da estrutura do DOM.
 * Sem o terceiro argumento vale a regra de sempre: só a troca de fazenda pede
 * confirmação. Com `blockRouteExit`, qualquer saída de rota também.
 */
export function useFinancialFormGuard(
  open: boolean,
  submitting: boolean,
  options: { dirty?: boolean; blockRouteExit?: boolean } = {},
) {
  const { registerForm } = useContext(NavigationContext);
  const dirty = options.dirty ?? true;
  const blockRouteExit = options.blockRouteExit ?? false;
  useLayoutEffect(() => {
    if (open) return registerForm({ submitting, dirty, blockRouteExit });
    return undefined;
  }, [open, registerForm, submitting, dirty, blockRouteExit]);
}

/** Único ponto de leitura/escrita de ?fazenda= e de proteção da transição. */
export function FinancialFarmNavigation({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    farms, financialScope, canSelectAllFarms, selectionVersion,
    applyFinancialScope, leaveAllFarms, registerBeforeFarmChange,
  } = useContext(FarmContext);
  const financial = getNavigationArea(location.pathname) === 'financial';
  const forms = useRef(new Set<FormState>());
  const confirmation = useRef<((accepted: boolean) => void) | null>(null);
  const [confirming, setConfirming] = useState<'farm' | 'exit' | null>(null);
  const [filterValues] = useState(() => new Map<string, unknown>());
  useLayoutEffect(() => { filterValues.clear(); }, [filterValues, location.pathname]);
  const [transition, setTransition] = useState(() => {
    const initial = { key: '', version: selectionVersion, scope: financialScope };
    return { from: initial, to: initial };
  });
  // Compara transições durante o render; o efeito abaixo fica responsável
  // somente por sincronizar contexto e URL antes de montar consultas.
  if ((!financial || farms) && (transition.to.key !== location.key ||
    transition.to.version !== selectionVersion || transition.to.scope !== financialScope)) {
    setTransition({ from: transition.to, to: { key: location.key, version: selectionVersion, scope: financialScope } });
  }
  const previous = transition.from;
  const params = new URLSearchParams(location.search);
  const requested = params.get('fazenda');
  const validScope = (scope: string) => scope === ALL_FARMS
    ? canSelectAllFarms && Boolean(farms?.length)
    : Boolean(farms?.some((farm) => farm.id === scope));
  const newNavigation = previous.key !== location.key;
  const selectedByUser = previous.version !== selectionVersion;
  const invalidLink = financial && requested !== null && !validScope(requested);
  const pendingLink = financial && newNavigation && !selectedByUser && requested !== null &&
    !invalidLink && requested !== financialScope;
  const pendingSelection = selectedByUser && (requested !== financialScope ||
    (previous.scope !== financialScope && (params.has('compra') || params.has('conta'))));
  const ready = !financial || Boolean(farms && !pendingLink && !pendingSelection);

  const registerForm = useCallback((form: FormState) => {
    forms.current.add(form);
    return () => { forms.current.delete(form); };
  }, []);

  const isSubmitting = useCallback(() => queryClient.isMutating() > 0 ||
    Array.from(forms.current).some((form) => form.submitting), [queryClient]);

  const confirmChange = useCallback(async (kind: 'farm' | 'exit' = 'farm') => {
    if (!financial) return true;
    if (isSubmitting()) {
      toast.info(kind === 'farm' ? 'Aguarde o envio terminar para trocar de fazenda.' : 'Aguarde o envio terminar para sair.');
      return false;
    }
    if (forms.current.size === 0) return true;
    if (confirmation.current) return false;
    return await new Promise<boolean>((resolve) => {
      confirmation.current = resolve;
      setConfirming(kind);
    });
  }, [financial, isSubmitting]);
  const confirmFarmChange = useCallback(() => confirmChange('farm'), [confirmChange]);

  // Formulário ligado à rota e alterado: recarregar/fechar a aba avisa
  // (o navegador mostra a própria mensagem; não há como guardar o conteúdo).
  const exitGuarded = useCallback(() => Array.from(forms.current).some(
    (form) => form.blockRouteExit && (form.dirty || form.submitting)), []);
  useEffect(() => {
    if (!financial) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!exitGuarded()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => { window.removeEventListener('beforeunload', onBeforeUnload); };
  }, [exitGuarded, financial]);

  useLayoutEffect(() => registerBeforeFarmChange(confirmFarmChange), [confirmFarmChange, registerBeforeFarmChange]);
  useEffect(() => () => { confirmation.current?.(false); }, []);

  // Intercepta inclusive voltar/avançar, antes de desmontar o formulário antigo.
  const blockReason = useRef<'farm' | 'exit'>('farm');
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!financial) return false;
    const nextScope = getNavigationArea(nextLocation.pathname) === 'financial'
      ? new URLSearchParams(nextLocation.search).get('fazenda')
      : null;
    if (nextScope && validScope(nextScope) && nextScope !== financialScope &&
      (forms.current.size > 0 || isSubmitting())) {
      blockReason.current = 'farm';
      return true;
    }
    const leaving = nextLocation.pathname !== currentLocation.pathname || nextLocation.search !== currentLocation.search;
    if (leaving && !closedByForm(nextLocation.state) && exitGuarded()) {
      blockReason.current = 'exit';
      return true;
    }
    return false;
  });
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    let active = true;
    void confirmChange(blockReason.current).then((accepted) => {
      if (!active) return;
      if (accepted) blocker.proceed();
      else blocker.reset();
    });
    return () => { active = false; };
  }, [blocker, confirmChange]);

  useLayoutEffect(() => {
    if (financial && !farms) return;
    const old = previous;
    if (!financial) {
      leaveAllFarms();
      return;
    }
    const nextParams = new URLSearchParams(location.search);
    if (selectedByUser && financialScope) {
      nextParams.set('fazenda', financialScope);
      if (old.scope !== financialScope) {
        nextParams.delete('compra');
        nextParams.delete('conta');
      }
    } else if (newNavigation && requested !== null) {
      if (!invalidLink && requested !== financialScope) applyFinancialScope(requested);
      return;
    } else if (requested === null && financialScope) {
      nextParams.set('fazenda', financialScope);
    }
    if (nextParams.toString() !== new URLSearchParams(location.search).toString()) {
      void navigate({ pathname: location.pathname, search: `?${nextParams.toString()}`, hash: location.hash }, { replace: true });
    }
  }, [applyFinancialScope, farms, financial, financialScope, invalidLink, leaveAllFarms,
    location.hash, location.key, location.pathname, location.search, navigate, newNavigation,
    previous, requested, selectedByUser, selectionVersion]);

  const finishConfirmation = (accepted: boolean) => {
    if (accepted && isSubmitting()) {
      toast.info('Aguarde o envio terminar.');
      accepted = false;
    }
    const resolve = confirmation.current;
    confirmation.current = null;
    setConfirming(null);
    resolve?.(accepted);
  };

  return (
    <NavigationContext.Provider value={{ registerForm, ready, invalidLink, filterValues }}>
      {children}
      <Dialog.Root open={confirming !== null}>
        <Dialog.Portal>
          <Dialog.Content asChild aria-describedby={undefined} onEscapeKeyDown={(event) => { event.preventDefault(); }}>
            <div role="presentation">
              <Dialog.Title style={{ display: 'none' }}>
                {confirming === 'exit' ? 'Confirmação de saída do formulário' : 'Confirmação de troca de fazenda'}
              </Dialog.Title>
              <ConfirmDialog
                portal={false}
                open={confirming !== null}
                title={confirming === 'exit' ? 'Sair sem salvar?' : 'Trocar de fazenda?'}
                message="O formulário aberto será fechado e os dados não salvos serão descartados."
                confirmLabel={confirming === 'exit' ? 'Sair sem salvar' : 'Fechar e trocar'}
                cancelLabel="Continuar preenchendo"
                onConfirm={() => { finishConfirmation(true); }}
                onCancel={() => { finishConfirmation(false); }}
              />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </NavigationContext.Provider>
  );
}

/** Mantém o shell disponível e só monta consultas após resolver o escopo. */
export function FinancialFarmContent({ children }: { children: ReactNode }) {
  const { ready, invalidLink } = useContext(NavigationContext);
  const { farms, farmsLoading, farmsError, financialScope } = useContext(FarmContext);
  const location = useLocation();
  const navigate = useNavigate();
  if (getNavigationArea(location.pathname) !== 'financial') return <>{children}</>;
  if (farmsError) return <FarmScopeNotice title="Fazendas indisponíveis" alert>Não foi possível carregar as fazendas. Recarregue a página para tentar novamente.</FarmScopeNotice>;
  if (farmsLoading || !ready) return <FarmScopeNotice title="Carregando fazendas">Carregando fazendas…</FarmScopeNotice>;
  if (invalidLink) return (
    <FarmScopeNotice title="Confira a fazenda do link" alert action={<EmptyStateAction type="button" onClick={() => {
        const next = new URLSearchParams(location.search);
        next.delete('fazenda');
        next.delete('conta');
        next.delete('compra');
        void navigate({ pathname: location.pathname, search: `?${next.toString()}`, hash: location.hash }, { replace: true });
      }}>Usar seleção atual</EmptyStateAction>}>
      A fazenda deste link é inválida ou não está disponível para seu usuário. Escolha uma fazenda no seletor lateral.
    </FarmScopeNotice>
  );
  if (farms?.length && !financialScope) return <FarmScopeNotice title="Selecione uma fazenda">Escolha uma fazenda no seletor lateral para abrir o Financeiro.</FarmScopeNotice>;
  return <Fragment key={financialScope}>{children}</Fragment>;
}

function FarmScopeNotice({ title, alert = false, action, children }: { title: string; alert?: boolean; action?: ReactNode; children: ReactNode }) {
  return <EmptyStateWrapper role={alert ? 'alert' : 'status'}>
    <EmptyStateCard>
      <EmptyStateTitle>{title}</EmptyStateTitle>
      <EmptyStateDescription>{children}</EmptyStateDescription>
      {action}
    </EmptyStateCard>
  </EmptyStateWrapper>;
}
